import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { transactionService, categoryService, accountService, reportService } from '../services/services';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const Transactions = () => {
  // Funções helper para datas
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    type: '',
    category_id: '',
  });
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    amount: '',
    type: 'expense',
    category_id: '',
    account_id: '',
    description: '',
    receipt_url: '',
  });

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await transactionService.list(filters);
      setTransactions(data);
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCategories();
    loadAccounts();
    loadTransactions();
  }, [filters, loadTransactions]);

  const loadCategories = async () => {
    try {
      const data = await categoryService.list();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService.list();
      setAccounts(data);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTransaction) {
        await transactionService.update(editingTransaction.id, formData);
      } else {
        await transactionService.create(formData);
      }
      setShowModal(false);
      setEditingTransaction(null);
      setFormData({
        date: getTodayDate(),
        amount: '',
        type: 'expense',
        category_id: '',
        account_id: '',
        description: '',
        receipt_url: '',
      });
      loadTransactions();
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao salvar transação');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      date: transaction.date,
      amount: transaction.amount,
      type: transaction.type,
      category_id: transaction.category_id || '',
      account_id: transaction.account_id || '',
      description: transaction.description || '',
      receipt_url: transaction.receipt_url || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ show: true, id });
  };

  const confirmDelete = async () => {
    if (deleteConfirm.id) {
      try {
        await transactionService.delete(deleteConfirm.id);
        loadTransactions();
        setDeleteConfirm({ show: false, id: null });
      } catch (error) {
        alert(error.response?.data?.error || 'Erro ao excluir transação');
        setDeleteConfirm({ show: false, id: null });
      }
    }
  };

  const handleExport = async () => {
    try {
      await transactionService.export(filters);
    } catch (error) {
      alert('Erro ao exportar transações');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Buscar transações e resumo
      const [transactionsData, summaryData] = await Promise.all([
        transactionService.list(filters),
        reportService.summary(filters.from, filters.to)
      ]);

      // Calcular estatísticas
      const totalTransactions = transactionsData.length;
      const incomeTransactions = transactionsData.filter(t => t.type === 'income').length;
      const expenseTransactions = transactionsData.filter(t => t.type === 'expense').length;
      const averageIncome = incomeTransactions > 0 ? summaryData.total_income / incomeTransactions : 0;
      const averageExpense = expenseTransactions > 0 ? summaryData.total_expense / expenseTransactions : 0;

      // Criar PDF
      const doc = new jsPDF();
      
      // Configurações
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = margin;

      // Título
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Transações', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Período
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      const periodo = `Período: ${formatDate(filters.from)} até ${formatDate(filters.to)}`;
      doc.text(periodo, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Resumo Financeiro
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumo Financeiro', margin, yPos);
      yPos += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      const resumoData = [
        ['Total de Entradas', formatCurrency(summaryData.total_income)],
        ['Quantidade de Entradas', incomeTransactions + ' transações'],
        ['Média por Entrada', formatCurrency(averageIncome)],
        ['Total de Saídas', formatCurrency(summaryData.total_expense)],
        ['Quantidade de Saídas', expenseTransactions + ' transações'],
        ['Média por Saída', formatCurrency(averageExpense)],
        ['Saldo Final', formatCurrency(summaryData.balance)],
        ['Total de Transações', totalTransactions + ' transações']
      ];

      autoTable(doc, {
        startY: yPos,
        head: [['Item', 'Valor']],
        body: resumoData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        bodyStyles: { textColor: [0, 0, 0] },
        margin: { left: margin, right: margin },
        styles: { fontSize: 10 }
      });

      yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 15 : yPos + 50;

      // Detalhes das Transações
      if (transactionsData.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Detalhes das Transações', margin, yPos);
        yPos += 8;

        const tableData = transactionsData.map(t => [
          formatDate(t.date),
          formatCurrency(t.amount),
          t.type === 'income' ? 'Entrada' : 'Saída',
          t.category_name || '—',
          t.account_name || '—',
          (t.description || '').substring(0, 30)
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Data', 'Valor', 'Tipo', 'Categoria', 'Conta', 'Descrição']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
          bodyStyles: { textColor: [0, 0, 0] },
          margin: { left: margin, right: margin },
          styles: { fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 35, halign: 'right' },
            2: { cellWidth: 25 },
            3: { cellWidth: 40 },
            4: { cellWidth: 35 },
            5: { cellWidth: 40 }
          }
        });
      }

      // Rodapé
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
        doc.text(
          `Gerado em ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 5,
          { align: 'center' }
        );
      }

      // Salvar PDF
      const fileName = `transacoes_${format(new Date(), 'yyyy-MM-dd', { locale: ptBR })}.pdf`;
      doc.save(fileName);
    } catch (error) {
      alert('Erro ao exportar PDF');
      console.error(error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Função para formatar data evitando problemas de timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Se a data vem como YYYY-MM-DD, criar como data local
    const date = dateString.includes('T') 
      ? parseISO(dateString) 
      : new Date(dateString + 'T00:00:00');
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Transações</h1>
          <p className="text-sm text-gray-400 mt-1.5">Gerencie suas transações financeiras</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportPDF}
            className="btn-secondary"
          >
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Exportar PDF
          </button>
          <button
            onClick={handleExport}
            className="btn-secondary"
          >
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exportar CSV
          </button>
          <button
            onClick={() => {
              setEditingTransaction(null);
              setFormData({
                date: getTodayDate(),
                amount: '',
                type: 'expense',
                category_id: '',
                account_id: '',
                description: '',
                receipt_url: '',
              });
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Transação
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Data Inicial</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              className="input-modern"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Data Final</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              className="input-modern"
            />
          </div>
          <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">Tipo</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="input-modern"
            >
              <option value="">Todos</option>
              <option value="income">Entrada</option>
              <option value="expense">Saída</option>
            </select>
          </div>
          <div>
                    <label className="block text-xs font-semibold text-gray-300 mb-1.5">Categoria</label>
            <select
              value={filters.category_id}
              onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
              className="input-modern"
            >
              <option value="">Todas</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-gray-300">Carregando transações...</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="table-header">Data</th>
                  <th className="table-header">Descrição</th>
                  <th className="table-header">Categoria</th>
                  <th className="table-header">Conta</th>
                  <th className="table-header">Tipo</th>
                  <th className="table-header text-right">Valor</th>
                  <th className="table-header">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-800">
                {transactions.length > 0 ? (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="transition-colors">
                      <td className="table-cell text-gray-200 font-medium">
                        {formatDate(transaction.date)}
                      </td>
                      <td className="table-cell text-gray-200">
                        {transaction.description || <span className="text-gray-500">—</span>}
                      </td>
                      <td className="table-cell text-gray-200">
                        {transaction.category_name || <span className="text-gray-500">—</span>}
                      </td>
                      <td className="table-cell text-gray-200">
                        {transaction.account_name || <span className="text-gray-500">—</span>}
                      </td>
                      <td className="table-cell">
                        <span className={`badge-${transaction.type === 'income' ? 'success' : 'danger'}`}>
                          {transaction.type === 'income' ? 'Entrada' : 'Saída'}
                        </span>
                      </td>
                      <td className={`table-cell text-right font-semibold ${
                        transaction.type === 'income' ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {transaction.type === 'income' ? '+' : '−'} {formatCurrency(transaction.amount)}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="btn-ghost text-xs p-1.5"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(transaction.id)}
                            className="btn-ghost text-xs p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm text-gray-400">Nenhuma transação encontrada</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="text-lg font-bold text-white">
                  {editingTransaction ? 'Editar Transação' : 'Nova Transação'}
                </h3>
                <p className="text-xs text-gray-300 mt-1">Preencha os dados da transação</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="modal-body">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Data</label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="input-modern"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Valor</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="input-modern"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Tipo</label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="input-modern"
                    >
                      <option value="income">Entrada</option>
                      <option value="expense">Saída</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Categoria</label>
                    <select
                      required
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="input-modern"
                    >
                      <option value="">Selecione...</option>
                      {categories.filter(c => c.type === formData.type).map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Conta</label>
                    <select
                      value={formData.account_id}
                      onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                      className="input-modern"
                    >
                      <option value="">Nenhuma</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Descrição</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input-modern"
                      placeholder="Descrição da transação"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">URL do Comprovante</label>
                    <input
                      type="url"
                      value={formData.receipt_url}
                      onChange={(e) => setFormData({ ...formData, receipt_url: e.target.value })}
                      className="input-modern"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingTransaction(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteConfirm.show && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm({ show: false, id: null })}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center border border-red-800/50">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Confirmar Exclusão</h3>
                  <p className="text-xs text-gray-300 mt-1">Esta ação não pode ser desfeita</p>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <p className="text-sm text-gray-300">
                Tem certeza que deseja excluir esta transação?
              </p>
            </div>
            <div className="modal-footer">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm({ show: false, id: null })}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg shadow-lg hover:bg-red-700 hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;

